import React, { useRef, useState } from 'react';
import { AlertTriangleIcon, CameraIcon, CheckIcon, CloudOffIcon, MapPinIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Field, Input, Textarea } from '../ui/Field';
import { canSubmit, evidenceFlags, metresBetween, missingEvidence, VERIFICATION_RADIUS_M } from '../../utils/network';
import type { CapturedEvidence, FieldTask } from '../../types/network';
type GpsState = 'idle' | 'locating' | 'captured' | 'denied' | 'unsupported';

/**
 * The field capture form. A representative cannot simply declare a property
 * verified: every configured requirement must be satisfied before submit
 * unlocks, and anomalies are surfaced to them before a reviewer ever sees them.
 */
export function EvidenceCapture({
  task,
  onSubmit,
  onCancel
}: {
  task: FieldTask;
  onSubmit: (captured: CapturedEvidence, offline: boolean) => void;
  onCancel: () => void;
}) {
  const [captured, setCaptured] = useState<CapturedEvidence>({
    gps: null,
    photos: [],
    declaration: false,
    fields: {}
  });
  const [gpsState, setGpsState] = useState<GpsState>('idle');
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pendingKey = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const outstanding = missingEvidence(task.evidence, captured);
  const ready = canSubmit(task, captured);
  const distance = task.expectedLocation && captured.gps ? metresBetween(task.expectedLocation, captured.gps) : null;
  const liveFlags = evidenceFlags(captured, distance, []);
  function captureLocation() {
    if (!('geolocation' in navigator)) {
      setGpsState('unsupported');
      setGpsError('This device does not expose location. A reviewer will confirm the address another way.');
      return;
    }
    setGpsState('locating');
    setGpsError(null);
    navigator.geolocation.getCurrentPosition(position => {
      setCaptured(current => ({
        ...current,
        gps: {
          lat: Number(position.coords.latitude.toFixed(6)),
          lng: Number(position.coords.longitude.toFixed(6)),
          accuracyM: Math.round(position.coords.accuracy),
          at: new Date().toISOString()
        }
      }));
      setGpsState('captured');
    }, positionError => {
      setGpsState('denied');
      setGpsError(positionError.code === positionError.PERMISSION_DENIED ? 'Location permission was refused. Grant it in your browser settings, or submit without it and expect a review flag.' : 'Location could not be read just now. Move into the open and try again.');
    }, {
      enableHighAccuracy: true,
      timeout: 12000,
      maximumAge: 0
    });
  }
  function pickPhoto(key: string) {
    pendingKey.current = key;
    fileInput.current?.click();
  }
  function onFile(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    const key = pendingKey.current;
    event.target.value = '';
    if (!file || !key) return;
    const requirement = task.evidence.find(item => item.key === key);
    const reader = new FileReader();
    reader.onload = () => {
      setCaptured(current => ({
        ...current,
        photos: [...current.photos.filter(photo => photo.key !== key), {
          key,
          label: requirement?.label ?? key,
          at: new Date().toISOString(),
          dataUrl: String(reader.result)
        }]
      }));
    };
    reader.onerror = () => setError('That image could not be read. Try capturing it again.');
    reader.readAsDataURL(file);
  }
  function setFieldValue(key: string, value: string) {
    setCaptured(current => ({
      ...current,
      fields: {
        ...current.fields,
        [key]: value
      }
    }));
  }
  function submit() {
    if (!ready) return;
    setBusy(true);
    setError(null);
    window.setTimeout(() => {
      setBusy(false);
      onSubmit(captured, offline);
    }, 450);
  }
  const photoRequirements = task.evidence.filter(item => item.kind === 'photo' || item.kind === 'video');
  const fieldRequirements = task.evidence.filter(item => item.kind === 'field');
  const needsGps = task.evidence.some(item => item.kind === 'gps');
  const needsDeclaration = task.evidence.some(item => item.kind === 'declaration');
  return <div className="space-y-4" data-id="element-3340" data-mp="1wcsbrh5j9ll">
      <input ref={fileInput} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" aria-hidden="true" tabIndex={-1} data-id="element-3341" data-mp="1vl8tf0tx2bo" />

      <div className="rounded-card border border-line bg-surface px-4 py-4" data-id="element-3342" data-mp="bs69xp9q9ta8">
        <div className="flex flex-wrap items-center gap-2" data-id="element-3343" data-mp="6wqyt533ubhi">
          <Badge tone={task.priority === 'Urgent' ? 'danger' : task.priority === 'Priority' ? 'warn' : 'neutral'} data-id="element-3344" data-mp="qgpxheajseox">
            {task.priority}
          </Badge>
          <Badge tone="neutral" data-id="element-3345" data-mp="vdq4psj4vnhm">{task.type}</Badge>
          <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-3346" data-mp="ghs1jqaslivw">{task.id}</span>
        </div>
        <h2 className="mt-2 text-lg font-semibold tracking-tight text-ink" data-id="element-3347" data-mp="lgv7re2idv5g">{task.title}</h2>
        <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-3348" data-mp="7q8alqcecdbh">{task.detail}</p>
        <p className="mt-2 text-[12px] text-ink-subtle" data-id="element-3349" data-mp="chq8g5j4hxs8">
          Due {task.dueOn} · {outstanding.length === 0 ? 'All required evidence captured' : `${outstanding.length} requirement${outstanding.length === 1 ? '' : 's'} outstanding`}
        </p>
      </div>

      {needsGps && <Panel title="Location" description="Captured while you are standing at the property." data-id="element-3350" data-mp="3kx05gz85oct5">
          {captured.gps ? <div className="space-y-2" data-id="element-3351" data-mp="3oyv601i9im5v">
              <p className="flex flex-wrap items-center gap-2" data-id="element-3352" data-mp="3oxttdslllrm1">
                <Badge tone="ok" icon={<CheckIcon className="h-3.5 w-3.5" data-id="element-3354" data-mp="1vhh2l1lv6ao3" />} data-id="element-3353" data-mp="1vgxx4kdeheqy">
                  Location captured
                </Badge>
                <span className="cz-num text-[12px] text-ink-muted" data-id="element-3355" data-mp="1qn34usjqbrg9">
                  {captured.gps.lat}, {captured.gps.lng} · ±{captured.gps.accuracyM}m
                </span>
              </p>
              {distance !== null && <p className={`text-[12px] leading-5 ${distance > VERIFICATION_RADIUS_M ? 'text-warn-700' : 'text-ink-muted'}`} data-id="element-3356" data-mp="2eeiaovapsqw9">
                  {distance}m from the expected property location
                  {distance > VERIFICATION_RADIUS_M ? ` — outside the ${VERIFICATION_RADIUS_M}m radius, so this will be flagged for a reviewer.` : '.'}
                </p>}
              <Button size="sm" variant="secondary" icon={<RefreshCwIcon className="h-3.5 w-3.5" data-id="element-3358" data-mp="30n5y49xqrv71" />} onClick={captureLocation} data-id="element-3357" data-mp="p02askuc4c8w">
                Recapture
              </Button>
            </div> : <>
              <Button variant="primary" size="sm" icon={<MapPinIcon className="h-3.5 w-3.5" data-id="element-3360" data-mp="3eeao0kbb5946" />} onClick={captureLocation} disabled={gpsState === 'locating'} data-id="element-3359" data-mp="39nc5rf6re0ya">
                {gpsState === 'locating' ? 'Locating…' : 'Capture location'}
              </Button>
              {gpsError && <p className="mt-2 flex items-start gap-2 text-[12px] leading-5 text-warn-700" data-id="element-3361" data-mp="3seby8qdgpswb">
                  <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-3362" data-mp="18258biip3sm" />
                  {gpsError}
                </p>}
            </>}
        </Panel>}

      {photoRequirements.length > 0 && <Panel title="Photographic evidence" description="Captured now, not chosen from an old gallery." data-id="element-3363" data-mp="22ym3dzlj0m6j">
          <ul className="grid gap-3 sm:grid-cols-2" data-id="element-3364" data-mp="8qi894vz8f02">
            {photoRequirements.map(requirement => {
          const photo = captured.photos.find(item => item.key === requirement.key);
          return <li key={requirement.key} className="rounded-lg border border-line-strong bg-canvas px-3.5 py-3" data-id="element-3365" data-mp="igl89p1imt7w">
                  <p className="flex flex-wrap items-center gap-2" data-id="element-3366" data-mp="3lz4rk227uoqk">
                    <span className="text-[13px] font-medium text-ink" data-id="element-3367" data-mp="3hwrmlfvamuuu">{requirement.label}</span>
                    {requirement.required ? <Badge tone={photo ? 'ok' : 'warn'} data-id="element-3368" data-mp="3vx2emm8cafhl">{photo ? 'Captured' : 'Required'}</Badge> : <Badge tone="neutral" data-id="element-3369" data-mp="3qz99zxmoptjf">Optional</Badge>}
                  </p>
                  {photo && <img src={photo.dataUrl} alt={`${requirement.label} evidence`} className="mt-2 h-32 w-full rounded-md border border-line object-cover" data-id="element-3370" data-mp="1677edarym3f9" />}
                  <div className="mt-2 flex flex-wrap gap-2" data-id="element-3371" data-mp="2t1kg3hrykkpi">
                    <Button size="sm" variant={photo ? 'secondary' : 'primary'} icon={<CameraIcon className="h-3.5 w-3.5" data-id="element-3373" data-mp="2k38szpqaw51t" />} onClick={() => pickPhoto(requirement.key)} data-id="element-3372" data-mp="2f4xidcs6lqij">
                      {photo ? 'Recapture' : 'Capture'}
                    </Button>
                    {photo && <Button size="sm" variant="ghost" icon={<XIcon className="h-3.5 w-3.5" data-id="element-3375" data-mp="330jpj2kjskdk" />} onClick={() => setCaptured(current => ({
                ...current,
                photos: current.photos.filter(item => item.key !== requirement.key)
              }))} data-id="element-3374" data-mp="2y1p0hivzlzl0">
                        Remove
                      </Button>}
                  </div>
                </li>;
        })}
          </ul>
        </Panel>}

      {fieldRequirements.length > 0 && <Panel title="Property record" description="Structured so reviewers and listings read the same values." data-id="element-3376" data-mp="3r1y7t3yle8bd">
          <div className="grid gap-3 sm:grid-cols-2" data-id="element-3377" data-mp="3dath4rd7cqd6">
            {fieldRequirements.map(requirement => <Field key={requirement.key} label={requirement.label} hint={requirement.required ? undefined : 'Optional'} data-id="element-3378" data-mp="3i4xohbqdr0es">
                {requirement.key === 'observation' || requirement.key === 'owner' ? <Textarea rows={2} value={captured.fields[requirement.key] ?? ''} onChange={event => setFieldValue(requirement.key, event.target.value)} data-id="element-3379" data-mp="2udsa71038g6l" /> : <Input value={captured.fields[requirement.key] ?? ''} onChange={event => setFieldValue(requirement.key, event.target.value)} data-id="element-3380" data-mp="2as04hbe4dqqn" />}
              </Field>)}
          </div>
        </Panel>}

      {liveFlags.length > 0 && <Panel title="This submission will be flagged for review" data-id="element-3381" data-mp="2yo4gunb4m95q">
          <ul className="space-y-1.5" data-id="element-3382" data-mp="2tquk0mleqv7v">
            {liveFlags.map(flag => <li key={flag} className="flex items-start gap-2 text-[12px] leading-5 text-warn-700" data-id="element-3383" data-mp="38f8v1ksz9pzi">
                <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-3384" data-mp="33g5tiac7i1wa" />
                {flag}
              </li>)}
          </ul>
          <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-3385" data-mp="3hgoy17hgqptp">
            A flag is not a rejection. It routes your submission to a reviewer who will look at the
            evidence before deciding.
          </p>
        </Panel>}

      {needsDeclaration && <Panel title="Declaration" data-id="element-3386" data-mp="2f1poinzq6og8">
          <label className="flex items-start gap-2.5" data-id="element-3387" data-mp="2f30v0vkk2ab1">
            <input type="checkbox" checked={captured.declaration} onChange={event => setCaptured(current => ({
          ...current,
          declaration: event.target.checked
        }))} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-3388" data-mp="2j3kn3kq9unqb" />
            <span className="text-[13px] leading-6 text-ink" data-id="element-3389" data-mp="2xrxpazfyd2t9">
              I visited this address in person today, captured this evidence myself, and recorded what
              I actually saw. I did not present myself as acting for the institution.
            </span>
          </label>
        </Panel>}

      <div className="rounded-card border border-line bg-surface px-4 py-4" data-id="element-3390" data-mp="18fdm9vo15c2w">
        <label className="flex items-start gap-2.5" data-id="element-3391" data-mp="1cevze59m0iv6">
          <input type="checkbox" checked={offline} onChange={event => setOffline(event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-3392" data-mp="1chh2sypk36v1" />
          <span data-id="element-3393" data-mp="v622lz2jawsd">
            <span className="flex items-center gap-1.5 text-[13px] font-medium text-ink" data-id="element-3394" data-mp="v5jx0btzf67s">
              <CloudOffIcon className="h-3.5 w-3.5" data-id="element-3395" data-mp="r59rf0a3qhtg" /> I am offline right now
            </span>
            <span className="mt-0.5 block text-[12px] leading-5 text-ink-muted" data-id="element-3396" data-mp="1nuqxhgczy5vo">
              Your work is held on this device and queued. It reaches a reviewer only once it syncs,
              and nothing is overwritten in the meantime.
            </span>
          </span>
        </label>

        {outstanding.length > 0 && <div className="mt-3 rounded-lg border border-line bg-canvas px-3.5 py-3" data-id="element-3397" data-mp="19u6eaj36681n">
            <p className="text-[12px] font-semibold text-ink" data-id="element-3398" data-mp="3ffe6hlver8o">
              Submit unlocks once these are captured
            </p>
            <ul className="mt-1 space-y-0.5" data-id="element-3399" data-mp="3ujvryss4znsr">
              {outstanding.map(requirement => <li key={requirement.key} className="text-[12px] leading-5 text-ink-muted" data-id="element-3400" data-mp="1mb7bdm8kpqir">
                  · {requirement.label}
                </li>)}
            </ul>
          </div>}

        {error && <p className="mt-3 flex items-start gap-2 text-[12px] leading-5 text-danger-700" data-id="element-3401" data-mp="1hdnpc05mw9g9">
            <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-3402" data-mp="1hbb6zlvyrx2o" />
            {error}
          </p>}

        <div className="mt-3 flex flex-wrap gap-2" data-id="element-3403" data-mp="2a232si94ajph">
          <Button variant="primary" onClick={submit} disabled={!ready || busy} data-id="element-3404" data-mp="2a1tc0sg908iq">
            {busy ? 'Submitting…' : offline ? 'Queue submission' : 'Submit for review'}
          </Button>
          <Button variant="ghost" onClick={onCancel} data-id="element-3405" data-mp="210bvxa2uc6u1">
            Save and close
          </Button>
        </div>
        <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-3406" data-mp="klty6y1opuwr">
          Submitting sends this for review. It does not mark the property Campuzon Verified — only a
          reviewer’s approval does that.
        </p>
      </div>
    </div>;
}